import { Fragment, useState } from "react";
import { XIcon } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export default function ListInput({
  id,
  value,
  onChange,
  error,
  required = false,
  disabled = false,
  placeholder = "Add item…",
  renderChip,
}) {
  const chips = Array.isArray(value) ? value : [];
  const [draft, setDraft] = useState("");

  const commitDraft = () => {
    const trimmed = draft.trim();
    if (!trimmed) return;
    if (chips.includes(trimmed)) {
      setDraft("");
      return;
    }
    onChange?.([...chips, trimmed]);
    setDraft("");
  };

  const removeChip = chip => {
    onChange?.(chips.filter(c => c !== chip));
  };

  const handleKeyDown = e => {
    if (e.key === "Enter") {
      e.preventDefault();
      commitDraft();
    } else if (e.key === "Backspace" && draft === "" && chips.length > 0) {
      e.preventDefault();
      onChange?.(chips.slice(0, -1));
    }
  };

  return (
    <div
      className={cn(
        "tw:flex tw:min-h-9 tw:flex-wrap tw:items-center tw:gap-1 tw:rounded-lg tw:border tw:border-input tw:bg-transparent tw:px-2.5 tw:py-1 tw:text-sm tw:transition-colors tw:focus-within:border-ring tw:focus-within:ring-3 tw:focus-within:ring-ring/50 tw:has-aria-invalid:border-destructive tw:has-aria-invalid:ring-3 tw:has-aria-invalid:ring-destructive/20 tw:dark:bg-input/30 tw:dark:has-aria-invalid:border-destructive/50 tw:dark:has-aria-invalid:ring-destructive/40",
        disabled && "tw:cursor-not-allowed tw:opacity-50"
      )}
    >
      {chips.map(c => {
        const removeButton = !disabled && (
          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            className="tw:opacity-50 tw:hover:opacity-100"
            onClick={() => removeChip(c)}
            aria-label={`Remove ${c}`}
          >
            <XIcon data-icon="inline-end" />
          </Button>
        );
        return renderChip ? (
          <Fragment key={c}>{renderChip(c, removeButton)}</Fragment>
        ) : (
          <Badge key={c} variant="secondary">
            {c}
            {removeButton}
          </Badge>
        );
      })}
      <input
        id={id}
        type="text"
        className="tw:min-w-16 tw:flex-1 tw:bg-transparent tw:outline-none tw:placeholder:text-muted-foreground"
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={commitDraft}
        placeholder={chips.length === 0 ? placeholder : ""}
        disabled={disabled}
        aria-required={required || undefined}
        aria-invalid={error ? "true" : undefined}
      />
    </div>
  );
}
